'use client';
import React from 'react';
import { BaristaOrder } from './baristaData';
import Icon from '@/components/ui/AppIcon';

interface OrderDetailModalProps {
  order: BaristaOrder;
  onClose: () => void;
  onAdvance: (id: string) => void;
  onComplete: (id: string) => void;
}

const statusLabel: Record<string, string> = {
  yeni: 'Yeni Sipariş',
  hazirlaniyor: 'Hazırlanıyor',
  hazir: 'Hazır',
};

const statusColor: Record<string, string> = {
  yeni: 'text-amber-400 bg-amber-500/20 border-amber-500/30',
  hazirlaniyor: 'text-blue-400 bg-blue-500/20 border-blue-500/30',
  hazir: 'text-green-400 bg-green-500/20 border-green-500/30',
};

export default function OrderDetailModal({ order, onClose, onAdvance, onComplete }: OrderDetailModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center animate-fade-in">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-[430px] bg-[#0D2118] border-t border-white/10 rounded-t-3xl animate-slide-up max-h-[80vh] overflow-y-auto scrollbar-hide">
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-white/20" />
        </div>

        <div className="px-5 py-4">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-white font-bold text-xl">{order.orderNumber}</h2>
              <p className="text-white/60 text-sm">{order.customerName}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs font-bold px-3 py-1 rounded-full border ${statusColor[order.status]}`}>
                {statusLabel[order.status]}
              </span>
              <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                <Icon name="XMarkIcon" size={18} className="text-white" />
              </button>
            </div>
          </div>

          {/* Meta */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-white/5 rounded-xl p-3 text-center">
              <p className="text-white/50 text-[10px] mb-1">Sipariş Tipi</p>
              <p className="text-white font-semibold text-xs">
                {order.orderType === 'gel-al' ? '🏃 Gel Al' : `🪑 Masa ${order.tableNumber}`}
              </p>
            </div>
            <div className="bg-white/5 rounded-xl p-3 text-center">
              <p className="text-white/50 text-[10px] mb-1">Saat</p>
              <p className="text-white font-semibold text-xs">{order.createdAt}</p>
            </div>
            <div className="bg-white/5 rounded-xl p-3 text-center">
              <p className="text-white/50 text-[10px] mb-1">Süre</p>
              <p className={`font-bold text-xs ${order.elapsedMinutes >= 10 ? 'text-red-400' : 'text-white'}`}>
                {order.elapsedMinutes} dk
              </p>
            </div>
          </div>

          {/* Items detail */}
          <div className="mb-4">
            <p className="text-white/50 text-xs font-semibold uppercase tracking-wider mb-2">Ürünler</p>
            <div className="space-y-2">
              {order.items.map((item) => (
                <div key={item.id} className="bg-white/5 rounded-xl p-3">
                  <div className="flex items-start justify-between mb-1">
                    <p className="text-white font-bold text-sm">{item.name}</p>
                    <span className="text-white/50 text-xs">×{item.quantity}</span>
                  </div>
                  {item.size !== '-' && (
                    <div className="flex flex-wrap gap-2">
                      <span className="text-[10px] bg-white/10 text-white/70 px-2 py-0.5 rounded-full">{item.size}</span>
                      <span className="text-[10px] bg-white/10 text-white/70 px-2 py-0.5 rounded-full">{item.milk}</span>
                      {item.syrup !== 'Yok' && (
                        <span className="text-[10px] bg-white/10 text-white/70 px-2 py-0.5 rounded-full">{item.syrup} Şurubu</span>
                      )}
                      {item.extraShot && (
                        <span className="text-[10px] bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full font-semibold">Ekstra Shot</span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          {order.notes && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3 mb-4">
              <div className="flex items-start gap-2">
                <Icon name="ExclamationTriangleIcon" size={16} className="text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-amber-300 font-semibold text-xs mb-0.5">Müşteri Notu</p>
                  <p className="text-amber-200 text-sm">{order.notes}</p>
                </div>
              </div>
            </div>
          )}

          {/* Total */}
          <div className="flex items-center justify-between py-3 border-t border-white/10 mb-4">
            <span className="text-white/60 text-sm">Toplam</span>
            <span className="text-white font-bold text-lg font-tabular">₺{order.totalPrice}</span>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            {order.status === 'yeni' && (
              <button
                onClick={() => { onAdvance(order.id); onClose(); }}
                className="flex-1 bg-blue-500/20 border border-blue-500/30 text-blue-400 font-bold py-4 rounded-2xl hover:bg-blue-500/30 transition-colors active:scale-[0.98]"
              >
                Hazırlamaya Başla
              </button>
            )}
            {order.status === 'hazirlaniyor' && (
              <button
                onClick={() => { onAdvance(order.id); onClose(); }}
                className="flex-1 bg-green-500/20 border border-green-500/30 text-green-400 font-bold py-4 rounded-2xl hover:bg-green-500/30 transition-colors active:scale-[0.98]"
              >
                Hazır ✓
              </button>
            )}
            {order.status === 'hazir' && (
              <button
                onClick={() => { onComplete(order.id); onClose(); }}
                className="flex-1 bg-primary/30 border border-primary/40 text-green-300 font-bold py-4 rounded-2xl hover:bg-primary/40 transition-colors active:scale-[0.98]"
              >
                Teslim Edildi ✓✓
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}